import { mockApiClient, realApiClient, type ApiMePayload, type ApiMembershipPayload, type ApiOrganizationPayload, type ApiUserPayload } from "./apiClient";
import { loginSchema, onboardingSchema, pharmacyRegistrationSchema } from "../schemas/authSchemas";

export interface AccountProfile {
  userId: string;
  email: string;
  status: string;
  emailVerified: boolean;
  primaryMembership?: ApiMembershipPayload;
  primaryOrganization?: ApiOrganizationPayload;
  memberships: ApiMembershipPayload[];
  organizations: ApiOrganizationPayload[];
  permissions: string[];
  verificationStatus: string;
}

function fallbackUser(): ApiUserPayload {
  const session = mockApiClient.getSession();
  return {
    userId: session.user.id,
    email: session.user.email,
    status: "active",
    emailVerified: true,
  };
}

export function mapApiProfile(payload: ApiMePayload): AccountProfile {
  const user = payload.user ?? fallbackUser();
  const primaryMembership = payload.memberships.find(membership => membership.status === "active") ?? payload.memberships[0];
  const primaryOrganization = payload.organizations.find(org => org.orgId === primaryMembership?.orgId) ?? payload.organizations[0];
  const permissions = [...new Set(payload.memberships.flatMap(membership => membership.permissions ?? []))];

  return {
    userId: user.userId,
    email: user.email,
    status: user.status,
    emailVerified: Boolean(user.emailVerified),
    primaryMembership,
    primaryOrganization,
    memberships: payload.memberships,
    organizations: payload.organizations,
    permissions,
    verificationStatus: primaryOrganization?.verificationStatus ?? "pending_verification",
  };
}

export const authService = {
  getSession: () => mockApiClient.getSession(),
  login: (input: unknown) => ({ session: mockApiClient.getSession(), input: loginSchema.parse(input) }),
  registerPharmacy: (input: unknown) => ({ id: "pharm-new", ...pharmacyRegistrationSchema.parse(input), status: "pending_verification" as const }),
  completeOnboarding: (input: unknown) => ({ ...onboardingSchema.parse(input), status: "complete" as const }),
  async loadProfile(): Promise<AccountProfile> {
    return mapApiProfile(await realApiClient.me());
  },
};
